/*
PROV: COHINT001.BF.9U8OQ2HZPP
REQ: SYS-ARCH-15
WHY: Next.js page module.
*/

import React, { useEffect } from "react";
import NextApp from "next/app";
import { useRouter } from "next/router";


import { ErrorBoundary } from "../components/ErrorBoundary";
import { ConsentManager } from "../components/ConsentManager";
import { AuthProvider } from "../contexts/AuthContext";
import "../styles/globals.css";
import "@cohorta/ui/tokens.css";
import "@cohorta/ui/editor/styles";
import { installBrowserErrorReporter } from "@cohorta/shared/telemetry/browserErrorReporter";
import { installNextRouteTimingReporter, reportWebVital } from "@cohorta/shared/telemetry/rum";
import { evaluateAnalyticsGate, trackPageView } from "../lib/gaAnalytics";
import { onConsentChange } from "../lib/consent";

function App({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    const uninstallErrors = installBrowserErrorReporter({ app: "support" });
    const uninstallRum = installNextRouteTimingReporter(router, { app: "support" });
    return () => {
      uninstallRum();
      uninstallErrors();
    };
  }, [router]);

  useEffect(() => {
    // ADR-007 loader gate: gtag is injected here at runtime, never from
    // _document.jsx, so no GA4 call fires before analytics consent is granted
    // (cohorta#439 contract). Dormant until that banner ships.
    evaluateAnalyticsGate();
    const unsubscribe = onConsentChange(evaluateAnalyticsGate);

    const handleRouteChange = (url) => trackPageView(url);
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      unsubscribe();
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);
  return (
    <ErrorBoundary>
      <AuthProvider>
      <Component {...pageProps} />
      <ConsentManager />
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Opt out of Automatic Static Optimisation so _document.jsx's
// getRuntimeConfigScript() runs on every request — required for runtime
// env-var injection (Firebase keys, etc.) when deployed on Cloud Run.
App.getInitialProps = async (appContext) => {
  return NextApp.getInitialProps(appContext);
};

export default App;

export function reportWebVitals(metric) {
  void reportWebVital(metric, { app: "support" });
}
